import { useState, useEffect, useCallback } from 'react';
import { toggleModelEnabled } from '../api/preferences';
import { useGetModelPreferences, useSetDefaultModel } from '../api/queryHooks';
import { useQueryClient } from '@tanstack/react-query';
import UserAuthStore from '../store/userStore';
import logger from '../services/Logger';
import useFeedback from './useFeedback';

const MODEL_PREFERENCES_KEY = 'photon_model_preferences';

export const useModelPreferences = () => {
  const [preferences, setPreferences] = useState({});
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const queryClient = useQueryClient();
  const { showFeedback } = useFeedback();

  // Load preferences via React Query (persisted to IndexedDB — available instantly on page load)
  const { data: prefsData, isLoading } = useGetModelPreferences(orgId, user?.uid, {
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Sync local preferences state from query data (onSuccess removed in React Query v5)
  useEffect(() => {
    if (prefsData?.data?.models) {
      const prefs = {};
      Object.keys(prefsData.data.models).forEach(modelId => {
        prefs[modelId] = prefsData.data.models[modelId].enabled;
      });
      setPreferences(prefs);
      localStorage.setItem(MODEL_PREFERENCES_KEY, JSON.stringify(prefs));
    }
  }, [prefsData]);

  // Derive defaultModel and preferences directly from cached query data
  const rawModels = prefsData?.data?.models || {};
  const defaultModel = Object.keys(rawModels).find(id => rawModels[id]?.is_default) || null;

  // RQ v5: enabled=false → isLoading=false even though data hasn't arrived.
  // Treat preferences as loading until data is available and auth/query are still pending.
  const preferencesSettled = prefsData !== undefined || (!isLoading && !!user?.uid && !!orgId);
  const effectiveLoading = !preferencesSettled;

  // Toggle model enabled/disabled state
  const toggleModel = async (modelId, isEnabled) => {
    if (!user?.uid || !orgId) {
      logger.warn('useModelPreferences', 'User or orgId not available for toggleModel', { modelId });
      return;
    }

    // Optimistic update — patch local state and cache
    const newPreferences = { ...preferences, [modelId]: isEnabled };
    setPreferences(newPreferences);
    localStorage.setItem(MODEL_PREFERENCES_KEY, JSON.stringify(newPreferences));

    const queryKey = ["model-preferences", orgId, user.uid];
    queryClient.setQueryData(queryKey, (old) => {
      if (!old?.data?.models) return old;
      const models = { ...old.data.models, [modelId]: { ...(old.data.models[modelId] || {}), enabled: isEnabled } };
      return { ...old, data: { ...old.data, models } };
    });

    // Persist to backend
    try {
      await toggleModelEnabled(orgId, user.uid, modelId, isEnabled);
    } catch (error) {
      logger.error('useModelPreferences', 'Error toggling model', { error: error.message, modelId, isEnabled });
      // Revert
      setPreferences(preferences);
      localStorage.setItem(MODEL_PREFERENCES_KEY, JSON.stringify(preferences));
      queryClient.invalidateQueries({ queryKey });
      // Previously silent — the toggle just snapped back with no explanation.
      showFeedback("Failed to update model preference. Please try again.", "error");
    }
  };

  // Set model as default using mutation hook
  const setDefaultModelMutation = useSetDefaultModel();
  const setModelAsDefault = async (modelId) => {
    if (!user?.uid || !orgId) {
      logger.warn('useModelPreferences', 'User or orgId not available for setModelAsDefault', { modelId });
      return;
    }

    // Optimistic update — patch the cached query data immediately
    const queryKey = ["model-preferences", orgId, user.uid];
    queryClient.setQueryData(queryKey, (old) => {
      if (!old?.data?.models) return old;
      const models = { ...old.data.models };
      Object.keys(models).forEach(id => { models[id] = { ...models[id], is_default: false }; });
      models[modelId] = { ...(models[modelId] || { enabled: true }), is_default: true };
      return { ...old, data: { ...old.data, models } };
    });

    // Persist to backend, then invalidate to sync actual server state
    setDefaultModelMutation.mutate(
      { orgId, userId: user.uid, modelId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey });
        },
        onError: (error) => {
          logger.error('useModelPreferences', 'Error setting default model', { error: error.message, modelId });
          // Revert by re-fetching
          queryClient.invalidateQueries({ queryKey });
          // Previously silent — the "Default" badge just reverted with no explanation.
          showFeedback("Failed to set default model. Please try again.", "error");
        },
      }
    );
  };

  // Check if a model is enabled — prefer cache source, fall back to local state
  const isModelEnabled = (modelId) => {
    if (modelId in rawModels) return rawModels[modelId]?.enabled !== false;
    return preferences[modelId] !== false;
  };

  // Check if a model is the default
  const isDefaultModel = useCallback((modelId) => {
    return defaultModel === modelId;
  }, [defaultModel]);

  // Pin/unpin model using mutation hook
  // (removed — pin feature removed)

  // Get only enabled models from a list
  const getEnabledModels = (models) => {
    if (!models) return [];
    return models.filter(model => isModelEnabled(model.id));
  };

  // Initialize preferences for new models (set to enabled by default)
  const initializeModels = (models) => {
    if (!models) return;
    
    const newPreferences = { ...preferences };
    let hasChanges = false;

    models.forEach(model => {
      if (!(model.id in newPreferences)) {
        newPreferences[model.id] = true;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setPreferences(newPreferences);
      localStorage.setItem(MODEL_PREFERENCES_KEY, JSON.stringify(newPreferences));
    }
  };

  return {
    preferences,
    defaultModel,
    toggleModel,
    setModelAsDefault,
    isModelEnabled,
    isDefaultModel,
    getEnabledModels,
    initializeModels,
    isLoading: effectiveLoading,
  };
};

export default useModelPreferences;